import { useState, useEffect, useCallback, useRef } from 'react';
import { getMyTeamLeavesToday, getMyTeamLeavesTimeline } from '../../services/myTeamLeavesService';
import { parseMonthKey, monthKey as toMonthKey, shiftMonth } from './teamLeavesModel';

// api.js GET okumalarında REJECT ETMEZ — gerçek sinyal res.data.status. İlk tünel isteği ay göndermez: sunucu içinde
// bulunulan ayı seçer ve gezinme penceresini (firstMonth/lastMonth) döner — istemci saati sınırı belirlemez.
// Hızlı ay değiştirmede geç gelen eski yanıt yenisini EZMEZ (useMyCalendar deseni: istek sıra numarası).
export function useMyTeamLeaves() {
  const [today, setToday] = useState({ loading: true, data: null, error: false });
  const [timeline, setTimeline] = useState({ loading: true, loadingMore: false, error: false, data: null, people: [] });
  const [month, setMonthKey] = useState(null);
  const lastRequest = useRef(0);

  useEffect(() => {
    let alive = true;
    getMyTeamLeavesToday()
      .then((res) => {
        if (!alive) return;
        if (res?.data?.status) setToday({ loading: false, data: res.data.data, error: false });
        else setToday({ loading: false, data: null, error: true });
      })
      .catch(() => { if (alive) setToday({ loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, []);

  const fetchPage = useCallback((key, page, append) => {
    const requestId = lastRequest.current + 1;
    lastRequest.current = requestId;
    setTimeline((prev) => (append
      ? { ...prev, loadingMore: true }
      : { loading: true, loadingMore: false, error: false, data: prev.data, people: [] }));

    const params = key ? { ...parseMonthKey(key), page } : { page };
    getMyTeamLeavesTimeline(params)
      .then((res) => {
        if (requestId !== lastRequest.current) return;
        if (!res?.data?.status) {
          setTimeline((prev) => ({ ...prev, loading: false, loadingMore: false, error: true }));
          return;
        }
        const data = res.data.data;
        setTimeline((prev) => ({
          loading: false,
          loadingMore: false,
          error: false,
          data,
          people: append ? [...prev.people, ...(data.people || [])] : (data.people || []),
        }));
        if (!key) setMonthKey(toMonthKey(data.year, data.month));
      })
      .catch(() => {
        if (requestId === lastRequest.current) setTimeline((prev) => ({ ...prev, loading: false, loadingMore: false, error: true }));
      });
  }, []);

  useEffect(() => { fetchPage(null, 1, false); }, [fetchPage]);

  const firstMonth = timeline.data?.firstMonth || null;
  const lastMonth = timeline.data?.lastMonth || null;
  const prevKey = month && firstMonth ? shiftMonth(month, -1, firstMonth, lastMonth) : null;
  const nextKey = month && firstMonth ? shiftMonth(month, 1, firstMonth, lastMonth) : null;

  const setMonth = useCallback((key) => {
    if (!key) return;
    setMonthKey(key);
    fetchPage(key, 1, false);
  }, [fetchPage]);

  const goPrev = useCallback(() => setMonth(prevKey), [setMonth, prevKey]);
  const goNext = useCallback(() => setMonth(nextKey), [setMonth, nextKey]);

  const loadMore = useCallback(() => {
    const data = timeline.data;
    if (!data || timeline.loadingMore || timeline.people.length >= data.totalPeople) return;
    fetchPage(month, data.page + 1, true);
  }, [fetchPage, month, timeline.data, timeline.loadingMore, timeline.people.length]);

  return {
    today,
    timeline,
    monthKey: month,
    firstMonth,
    lastMonth,
    canPrev: Boolean(prevKey),
    canNext: Boolean(nextKey),
    goPrev,
    goNext,
    setMonth,
    loadMore,
  };
}

export default useMyTeamLeaves;
